import React, { Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import FestivalContextProvider from './_features/FestivalContext'
import { LeaveApp } from './_features/LeaveApp'
import { LogoutRedirect } from './_features/LogoutRedirect'
import { NotFound } from './_features/NotFound'
import ScheduleContextProvider from './_features/ScheduleContext'
import UserContext from './_features/UserContext'
import { Websocket } from './_features/Websocket'
import { Layout } from './_layout'
import { ControllistPage } from './_pages/ControllistPage'
import { SeasonSectionsOverviewPage } from './_pages/SeasonSectionsOverviewPage'
import { Spinner } from './_ui'

const AllUsersPage = React.lazy(() => import('./_pages/AllUsersPage')
  .then(({ AllUsersPage }) => ({default: AllUsersPage}))
);
const CateringPage = React.lazy(() => import('./_pages/CateringPage')
  .then(({ CateringPage }) => ({default: CateringPage}))
);
const CateringScanPage = React.lazy(() => import('./_pages/CateringScanPage')
  .then(({ CateringScanPage }) => ({default: CateringScanPage}))
);
const CrewPage = React.lazy(() => import('./_pages/CrewPage')
  .then(({ CrewPage }) => ({default: CrewPage}))
);
const CheckInPage = React.lazy(() => import('./_pages/CheckInPage')
  .then(({ CheckInPage }) => ({default: CheckInPage}))
);
const DashboardPage = React.lazy(() => import('./_pages/DashboardPage')
  .then(({ DashboardPage }) => ({default: DashboardPage}))
);
const EvaluationPage = React.lazy(() => import('./_pages/EvaluationPage')
  .then(({ EvaluationPage }) => ({default: EvaluationPage}))
);
const GroupsPage = React.lazy(() => import('./_pages/GroupsPage')
  .then(({ GroupsPage }) => ({default: GroupsPage}))
);
const GuestListOverviewPage = React.lazy(() => import('./_pages/GuestListOverviewPage')
  .then(({ GuestListOverviewPage }) => ({default: GuestListOverviewPage}))
);
const GuestListPage = React.lazy(() => import('./_pages/GuestListPage')
  .then(({ GuestListPage }) => ({default: GuestListPage}))
);
const MessagesPage = React.lazy(() => import('./_pages/MessagesPage')
  .then(({ MessagesPage }) => ({default: MessagesPage}))
);
const PassesPage = React.lazy(() => import('./_pages/PassesPage')
  .then(({ PassesPage }) => ({default: PassesPage}))
);
const SchedulePage = React.lazy(() => import('./_pages/SchedulePage')
  .then(({ SchedulePage }) => ({default: SchedulePage}))
);
const ScheduleOverviewPage = React.lazy(() => import('./_pages/ScheduleOverviewPage')
  .then(({ ScheduleOverviewPage }) => ({default: ScheduleOverviewPage}))
);
const ScheduleDetailsPage = React.lazy(() => import('./_pages/ScheduleDetailsPage')
  .then(({ ScheduleDetailsPage }) => ({default: ScheduleDetailsPage}))
);
const ScheduleApplicationPage = React.lazy(() => import('./_pages/ScheduleApplicationPage')
  .then(({ ScheduleApplicationPage }) => ({default: ScheduleApplicationPage}))
);
const MyFestivalPage = React.lazy(() => import('./_pages/MyFestivalPage')
  .then(({ MyFestivalPage }) => ({default: MyFestivalPage}))
);
const ScheduleSectionPage = React.lazy(() => import('./_pages/ScheduleSectionPage')
  .then(({ ScheduleSectionPage }) => ({default: ScheduleSectionPage}))
);
const ScheduleToConfirmPage = React.lazy(() => import('./_pages/ScheduleToConfirmPage')
  .then(({ ScheduleToConfirmPage }) => ({default: ScheduleToConfirmPage}))
);
const SelfCheckInPage = React.lazy(() => import('./_pages/SelfCheckInPage')
  .then(({ SelfCheckInPage }) => ({default: SelfCheckInPage}))
);
const SlotCheckinPage = React.lazy(() => import('./_pages/SlotCheckinPage')
  .then(({ SlotCheckinPage }) => ({default: SlotCheckinPage}))
);
const SettingsPage = React.lazy(() => import('./_pages/SettingsPage')
  .then(({ SettingsPage }) => ({default: SettingsPage}))
);
const InstructionPage = React.lazy(() => import('./_pages/InstructionPage')
  .then(({ InstructionPage }) => ({default: InstructionPage}))
);
const MissingScheduleSlotsPage = React.lazy(() => import('./_pages/MissingScheduleSlotsPage')
  .then(({ MissingScheduleSlotsPage }) => ({default: MissingScheduleSlotsPage}))
);
const UserWorkloadPage = React.lazy(() => import('./_pages/UserWorkloadPage')
  .then(({ UserWorkloadPage }) => ({default: UserWorkloadPage}))
);

export const AuthenticatedApp = () => {
  return (
    <Websocket>
      <ScheduleContextProvider>
        <UserContext loggedOut={<LogoutRedirect />}>
          <FestivalContextProvider>
            <SkeletonTheme>
              <Layout>
                <Suspense fallback={<Spinner />}>
                  <Routes>
                    <Route path={'/benutzer/*'} element={<AllUsersPage />} />
                    <Route path={'/crew/*'} element={<CrewPage pageLink={'/crew/'} />} />
                    <Route path={'/catering/:cateringMealID/*'} element={<CateringScanPage />} />
                    <Route path={'/catering/*'} element={<CateringPage />} />
                    <Route path={'/checkin/*'} element={<CheckInPage />} />
                    <Route path={'/self-checkin/group/:sectionRoleID/'} element={<SelfCheckInPage isGroup pageLink={'/self-checkin/group/'} />} />
                    <Route path={'/self-checkin/:sectionRoleID/'} element={<SelfCheckInPage pageLink={'/self-checkin/'} />} />
                    <Route path={'/self-checkin/*'} element={<SelfCheckInPage pageLink={'/self-checkin/'} />} />
                    <Route path={'/auswertung/*'} element={<EvaluationPage />} />
                    <Route path={'/gruppen/*'} element={<GroupsPage pageLink={'/gruppen/'} />} />
                    <Route path={'/nachrichten/*'} element={<MessagesPage pageLink={'/nachrichten/'} />} />
                    <Route path={'/schichtplan/offen/*'} element={<ScheduleToConfirmPage />} />
                    <Route path={'/schichtplan/bereich/*'} element={<ScheduleSectionPage pageLink={'/schichtplan/bereich/'} />} />
                    <Route path={'/schichtplan/helfer/:sectionID/details/*'} element={<ScheduleDetailsPage />} />
                    <Route path={'/schichtplan/helfer/alle'} element={<SeasonSectionsOverviewPage pageLink={'/schichtplan/helfer/'} />} />
                    <Route path={'/schichtplan/helfer/*'} element={<SchedulePage pageLink={'/schichtplan/helfer/'} />} />
                    <Route path={'/schichtplan/auswertung/*'} element={<MissingScheduleSlotsPage pageLink={'/schichtplan/auswertung/'} />} />
                    <Route path={'/schichtplan/kontrolllisten/*'} element={<ControllistPage />} />
                    <Route path={'/schichtplan/helfer-zeiten/*'} element={<UserWorkloadPage />} />
                    <Route path={'/schichtplan/*'} element={<ScheduleOverviewPage />} />
                    <Route path={'/mein-festival/bewerbung/*'} element={<ScheduleApplicationPage pageLink={'/mein-festival/bewerbung/'} overviewLink={'/mein-festival/'} />} />
                    <Route path={'/mein-festival/*'} element={<MyFestivalPage />} />
                    <Route path={'/gaesteliste/besucher/*'} element={<GuestListPage pageLink={'/gaesteliste/besucher/'} type={'Guest'} />} />
                    <Route path={'/gaesteliste/akkreditierung/*'} element={<GuestListPage pageLink={'/gaesteliste/akkreditierung/'} type={'Accreditation'} />} />
                    <Route path={'/gaesteliste/*'} element={<GuestListOverviewPage />} />
                    <Route path={'/ausweise/*'} element={<PassesPage />} />
                    <Route path={'/verwaltung/*'} element={<SettingsPage />} />
                    <Route path={'/schichten/*'} element={<SlotCheckinPage pageLink={'/schichten/'} />} />
                    <Route path={'/anleitung/*'} element={<InstructionPage />} />
                    <Route path={'/festivals/*'} element={<LeaveApp />} />
                    <Route path={'/passwort-vergessen'} element={<LeaveApp />} />
                    <Route path={'/passwort-zuruecksetzen'} element={<LeaveApp />} />
                    <Route path={'/registrieren'} element={<LeaveApp />} />
                    <Route path={'/login'} element={<Navigate to={'/'} replace />} />
                    <Route index element={<DashboardPage />} />
                    <Route path={'*'} element={<NotFound />} />
                  </Routes>
                </Suspense>
              </Layout>
            </SkeletonTheme>
          </FestivalContextProvider>
        </UserContext>
      </ScheduleContextProvider>
    </Websocket>
  )
}
